// @ts-nocheck
import { content } from './content.js';

const PUBLIC_CLAIMS = new Set(['confirmed', 'confirmed_with_limits']);
const PUBLIC_ERP_STAGES = new Set(['production_use', 'external_offering']);
const VALID_EVIDENCE_STATUSES = new Set(['documented', 'verified']);

export function localize(value, locale) { return value[locale] || value.ru; }
export function isPublishable(entity) { return entity?.status === 'published' && PUBLIC_CLAIMS.has(entity.claimStatus); }
export function isPublicErpStage(stage) { return PUBLIC_ERP_STAGES.has(stage); }

export function getPublishedSolutions() { return content.solutions.filter(isPublishable); }
export function getSolution(slug) { return getPublishedSolutions().find(solution => solution.slug === slug) || null; }
export function getPublishedFacts() { return content.facts.filter(fact => fact.enabled && isPublishable(fact)); }

export function getSoftwareProduct(slug) {
  const product = content.softwareProducts.find(item => item.slug === slug);
  if (!product || !isPublishable(product)) return null;
  if (slug === 'anlexx-erp' && !isPublicErpStage(product.publicStage)) return null;
  return product;
}

export function getTracoraFunctions() {
  return content.softwareFunctions.filter(item => {
    if (item.productId !== 'software-tracora' || !isPublishable(item)) return false;
    return item.evidenceIds.length > 0 && item.evidenceIds.every(evidenceId => {
      const evidence = content.functionEvidence.find(record => record.id === evidenceId);
      return evidence && evidence.functionId === item.id && isPublishable(evidence) && VALID_EVIDENCE_STATUSES.has(evidence.evidenceStatus);
    });
  });
}

export function getCatalogReadiness(model) {
  const reasons = [];
  if (!model) return { ready: false, reasons: ['MODEL_NOT_FOUND'] };
  if (!isPublishable(model)) reasons.push('MODEL_NOT_PUBLISHED');
  const manufacturer = content.manufacturers.find(item => item.id === model.manufacturerId);
  if (!manufacturer || !manufacturer.verified || !isPublishable(manufacturer)) reasons.push('MANUFACTURER_NOT_VERIFIED');
  const category = content.equipmentCategories.find(item => item.id === model.categoryId);
  if (!category || !isPublishable(category)) reasons.push('CATEGORY_NOT_PUBLISHED');
  if (model.lifecycleStatus === 'archived') reasons.push('MODEL_ARCHIVED');
  if (model.technicalClaimIds.length === 0) reasons.push('CLAIMS_MISSING');
  for (const claimId of model.technicalClaimIds) {
    const claim = content.technicalClaims.find(item => item.id === claimId);
    if (!claim || !isPublishable(claim) || !claim.source || claim.evidenceIds.length === 0) reasons.push('CLAIM_EVIDENCE_MISSING');
  }
  for (const mediaId of model.mediaIds) {
    const media = content.media.find(item => item.id === mediaId);
    if (!isMediaPublishable(media)) reasons.push('MEDIA_RIGHTS_MISSING');
  }
  return { ready: reasons.length === 0, reasons: [...new Set(reasons)] };
}

export function getPublishedModels() { return content.models.filter(model => getCatalogReadiness(model).ready); }
export function getModel(categorySlug, modelSlug) {
  const category = getPublishedCategories().find(item => item.slug === categorySlug);
  if (!category) return null;
  return getPublishedModels().find(model => model.categoryId === category.id && model.slug === modelSlug) || null;
}
export function getPublishedCategories() {
  const modelCategoryIds = new Set(getPublishedModels().map(model => model.categoryId));
  return content.equipmentCategories.filter(item => isPublishable(item) && modelCategoryIds.has(item.id));
}
export function getPublishedManufacturers() {
  const manufacturerIds = new Set(getPublishedModels().map(model => model.manufacturerId));
  return content.manufacturers.filter(item => item.verified && isPublishable(item) && manufacturerIds.has(item.id));
}
export function getManufacturer(slug) { return getPublishedManufacturers().find(item => item.slug === slug) || null; }
export function isModelAvailable(model) {
  if (!model || ['archived', 'discontinued'].includes(model.lifecycleStatus)) return false;
  return !['unavailable', 'unknown'].includes(model.supplyStatus);
}
export function getPublishedConsumables() { return content.consumables.filter(item => isPublishable(item) && item.compatibleModelIds.some(id => getPublishedModels().some(model => model.id === id))); }
export function getPublishedSpareParts() { return content.spareParts.filter(item => isPublishable(item) && item.compatibleModelIds.some(id => getPublishedModels().some(model => model.id === id))); }
export function getPublishedAccessories() { return content.accessories.filter(item => isPublishable(item) && item.compatibleModelIds.some(id => getPublishedModels().some(model => model.id === id))); }

export function getVerifiedCompatibilityRecords() {
  const publicModelIds = new Set(getPublishedModels().map(model => model.id));
  return content.compatibilityRecords.filter(record => {
    if (!isPublishable(record) || !publicModelIds.has(record.equipmentModelId)) return false;
    if (!['verified', 'verified_after_configuration', 'limited_configuration'].includes(record.compatibilityStatus)) return false;
    const version = content.softwareVersions.find(item => item.id === record.softwareVersionId && isPublishable(item));
    return Boolean(version && record.evidenceIds.length > 0 && record.reviewer && record.verifiedAt);
  });
}

export function isMediaPublishable(media) { return Boolean(media && isPublishable(media) && media.origin !== 'unknown' && media.rightsConfirmed); }
export function getPublishedDocuments() { return content.documents.filter(document => document.public && isPublishable(document)); }
export function getPublishedKnowledgeMaterials() {
  return content.knowledgeMaterials.filter(material => isPublishable(material) && material.evidenceIds.length > 0 && material.schemaType !== 'none');
}
export function getKnowledgeMaterial(slug) { return getPublishedKnowledgeMaterials().find(item => item.slug === slug) || null; }

export function getSeo(path) { return content.seo.find(record => record.path === path && isPublishable(record)) || null; }

export function getVerifiedContact(kind) {
  return content.contacts.find(contact => contact.kind === kind && contact.verified && isPublishable(contact)) || null;
}

export function getFormReadiness(formId) {
  const form = content.forms.find(item => item.id === formId);
  if (!form) return { ready: false, reasons: ['FORM_NOT_FOUND'] };
  const consent = content.consents.find(item => item.id === form.consentId);
  const route = content.formRoutes.find(item => item.id === form.routeId);
  const reasons = [];
  if (!consent?.approved) reasons.push('CONSENT_NOT_APPROVED');
  if (!route?.verified) reasons.push('ROUTE_NOT_VERIFIED');
  if (form.backendAdapter !== 'api') reasons.push('BACKEND_NOT_CONFIGURED');
  return { ready: reasons.length === 0, reasons };
}

export function buildPublishedSearchIndex(locale) {
  const items = [];
  for (const solution of getPublishedSolutions()) items.push({ id: solution.id, kind: 'solution', path: `/solutions/${solution.slug}`, title: localize(solution.title, locale), summary: localize(solution.summary, locale), tokens: [] });
  for (const category of getPublishedCategories()) items.push({ id: category.id, kind: 'equipment-category', path: `/equipment/${category.slug}`, title: localize(category.title, locale), summary: '', tokens: [] });
  for (const model of getPublishedModels()) items.push({ id: model.id, kind: 'equipment-model', path: `/equipment/${content.equipmentCategories.find(category => category.id === model.categoryId)?.slug}/${model.slug}`, title: localize(model.title, locale), summary: '', tokens: [model.slug] });
  for (const manufacturer of getPublishedManufacturers()) items.push({ id: manufacturer.id, kind: 'manufacturer', path: `/manufacturers/${manufacturer.slug}`, title: localize(manufacturer.title, locale), summary: '', tokens: [] });
  for (const technology of content.technologies.filter(isPublishable)) items.push({ id: technology.id, kind: 'technology', path: '/solutions', title: localize(technology.title, locale), summary: technology.summary ? localize(technology.summary, locale) : '', tokens: [] });
  for (const software of content.softwareProducts.filter(item => getSoftwareProduct(item.slug))) items.push({ id: software.id, kind: 'software', path: `/software/${software.slug}`, title: localize(software.title, locale), summary: '', tokens: software.functions });
  for (const service of content.services.filter(isPublishable)) items.push({ id: service.id, kind: 'service', path: '/service', title: localize(service.title, locale), summary: '', tokens: [service.requestType] });
  for (const material of getPublishedKnowledgeMaterials()) items.push({ id: material.id, kind: 'knowledge', path: `/knowledge/${material.slug}`, title: localize(material.title, locale), summary: localize(material.summary, locale), tokens: [] });
  for (const document of getPublishedDocuments()) items.push({ id: document.id, kind: 'document', path: document.url, title: localize(document.title, locale), summary: '', tokens: [document.version] });
  return items;
}

export function searchPublished(query, locale) {
  const needle = query.trim().toLocaleLowerCase(locale);
  if (!needle) return [];
  return buildPublishedSearchIndex(locale).filter(item => `${item.title} ${item.summary} ${item.tokens.join(' ')}`.toLocaleLowerCase(locale).includes(needle));
}
